/**
 * Experiência, subida de nível e ranques de arma.
 *
 * A experiência vem de AGIR, não de vencer: quem bate ganha, quem cura ganha, e
 * quem mata ganha bem mais. É o que sustenta o clérigo, que quase nunca mata, e
 * o que impede que só o herói mais forte cresça.
 */
import { capStats } from './stats.js';
import { STAT_KEYS } from './types.js';
export const EXP_PER_LEVEL = 100;
/** Ranques de arma, do pior ao melhor. */
export const WEAPON_RANKS = ['E', 'D', 'C', 'B', 'A', 'S'];
/** Experiência de arma acumulada para alcançar cada ranque. */
export const RANK_THRESHOLDS = {
    E: 0, D: 31, C: 71, B: 121, A: 181, S: 251,
};
/**
 * Experiência por acertar sem matar.
 *
 * Inspirada na série, não idêntica: bater em quem está acima do seu nível rende
 * mais, bater em quem está muito abaixo rende o mínimo, e nunca rende zero —
 * um golpe que não ensina nada desestimula usar a unidade fraca, que é
 * justamente a que precisa crescer.
 */
export function expForHit(unitLevel, targetLevel) {
    return Math.max(5, Math.floor((36 + (targetLevel - unitLevel) * 2) / 3));
}
/** Experiência por derrotar. O grosso do ganho, como na série. */
export function expForKill(unitLevel, targetLevel, boss = false) {
    const base = Math.max(45, expForHit(unitLevel, targetLevel) + 35);
    const gap = Math.max(0, targetLevel - unitLevel) * 4;
    return base + gap + (boss ? 40 : 0);
}
/** Experiência por curar: proporcional ao que foi curado, com piso. */
export function expForHeal(healed) {
    return Math.max(15, 12 + Math.floor(healed / 2));
}
/**
 * Aplica experiência e resolve a subida de nível, se houver.
 *
 * Cada atributo é sorteado contra a própria taxa de crescimento. Quando todos
 * os oito falham, um atributo é forçado — a "subida vazia" existe na série e é
 * odiada por bons motivos: gasta um dos vinte níveis da unidade sem nada em
 * troca. O atributo forçado é o de maior crescimento, que é o que o jogador
 * escolheu aquela classe para ter.
 */
export function gainExp(klass, level, exp, stats, amount, rng, maxLevel = 20) {
    let nextExp = exp + Math.max(0, amount);
    if (level >= maxLevel) {
        return { leveled: false, level, exp: Math.min(nextExp, EXP_PER_LEVEL - 1), gains: {}, stats };
    }
    if (nextExp < EXP_PER_LEVEL) {
        return { leveled: false, level, exp: nextExp, gains: {}, stats };
    }
    nextExp -= EXP_PER_LEVEL;
    const gains = {};
    let grown = 0;
    for (const key of STAT_KEYS) {
        if (rng.chance(klass.growths[key])) {
            gains[key] = 1;
            grown++;
        }
    }
    if (grown === 0) {
        let bestKey = 'hp';
        for (const key of STAT_KEYS) {
            if (klass.growths[key] > klass.growths[bestKey])
                bestKey = key;
        }
        gains[bestKey] = 1;
    }
    const next = { ...stats };
    for (const [key, value] of Object.entries(gains))
        next[key] = (next[key] ?? 0) + value;
    return {
        leveled: true,
        level: level + 1,
        exp: nextExp,
        gains: gains,
        stats: capStats(next),
    };
}
export function rankFromExp(exp) {
    let rank = 'E';
    for (const candidate of WEAPON_RANKS) {
        if (exp >= RANK_THRESHOLDS[candidate])
            rank = candidate;
    }
    return rank;
}
export function rankAtLeast(rank, required) {
    return WEAPON_RANKS.indexOf(rank) >= WEAPON_RANKS.indexOf(required);
}
/** Bônus de crítico por ranque alto — o prêmio por especializar a unidade. */
export function rankCritBonus(rank) {
    switch (rank) {
        case 'S': return 5;
        case 'A': return 3;
        case 'B': return 1;
        default: return 0;
    }
}
//# sourceMappingURL=progression.js.map